// Taken from Iowa
import { DatePipe } from "@angular/common";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import dayjs from "dayjs";
import { shortDateFormat } from "./dateTimeHelpers";

export const validationKeys = {
  noNumbers: "no-numbers",
  ascendPassword: "ascend-password",
  noNumbersLimitedSpecialChars: "no-numbers-limited-special-chars",
  zipCode: "zip-code",
  validUri: "valid-uri",
};

export const phoneValidator = Validators.pattern(
  /.*\d.*\d.*\d.*\d.*\d.*\d.*\d.*\d.*\d.*\d.*/
);

export const onlyNumberValidator = Validators.pattern("\\-?\\d*\\.?\\d{1,2}");

export const zipCodeValidator = createPatternValidator(
  /(^\d{5}$)|(^\d{5}-\d{4}$)/,
  validationKeys.zipCode
);
export const noNumbersValidator = createPatternValidator(
  /^[^\d]+$/,
  validationKeys.noNumbers
);
export const uriValidator = createPatternValidator(
  //https://stackoverflow.com/a/8234912
  /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+|(?:[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)/,
  validationKeys.validUri
);

// Still allow dashes - and apostrophes ' and periods .
export const noNumbersLimitedSpecialCharsValidator = createPatternValidator(
  /^[^\d~`!@#$%^&*()_+={}[\]:;"?/<,>]+$/,
  validationKeys.noNumbersLimitedSpecialChars
);

export const todayOrLater: ValidatorFn = (
  control: FormControl
): ValidationErrors | null => {
  const date = dayjs(control.value);
  if (date.isValid() && date.isBefore(dayjs().startOf("day"))) {
    return { todayOrLater: true };
  }
  return null;
};

export const tooOld =
  (age: number): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const dateToCompare = new Date();
    dateToCompare.setFullYear(dateToCompare.getFullYear() - age);
    return control.value < dateToCompare
      ? { tooOld: { value: control.value } }
      : null;
  };

// For FormControls that contain arrays
export const minimumCount =
  (x: number): ValidatorFn =>
  (control: FormControl): ValidationErrors | null => {
    return control.value?.length < x
      ? { minimumCount: { value: control.value.length } }
      : null;
  };

// For stateId requiring to be 10 characters long
export const requiredLength =
  (x: number): ValidatorFn =>
  (control: FormControl): ValidationErrors | null => {
    if (control.value?.length == 0) return null;
    else
      return control.value?.length < x || control.value?.length > x
        ? { lengthError: "This field must be " + x + " characters" }
        : null;
  };

export const passwordsMatchValidator: ValidatorFn = (
  formGroup: FormGroup
): ValidationErrors | null => {
  const password = formGroup.get("password")?.value;
  const confirmedPassword = formGroup.get("confirmedPassword")?.value;

  return password !== confirmedPassword ? { passwordMismatch: true } : null;
};

export const ascendPasswordValidator = createPatternValidator(
  /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/,
  validationKeys.ascendPassword
);

export function passwordPolicyValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || "";

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[\W_]/.test(value);
    const isLongEnough = value.length >= 12;

    const valid =
      hasUpperCase &&
      hasLowerCase &&
      hasNumber &&
      hasSpecialChar &&
      isLongEnough;

    return valid
      ? null
      : {
          passwordPolicy: {
            hasUpperCase,
            hasLowerCase,
            hasNumber,
            hasSpecialChar,
            isLongEnough,
          },
        };
  };
}

export function validateForm(form: FormGroup | FormArray, key: string): string {
  let text: string;
  let pipe = new DatePipe("en-US");

  if (!form) {
    return;
  }

  const control = form.get(key);

  if (!control) {
    return;
  }

  const controlErrors: ValidationErrors = control.errors;
  if (controlErrors != null) {
    Object.keys(controlErrors).forEach((keyError) => {
      switch (keyError) {
        case "uriValid":
          text = `Valid URL Required`;
          break;
        case "required":
          text = `Field is required!`;
          break;
        case "maxlength":
          text = `Max length: ${controlErrors[keyError].requiredLength}`;
          break;
        case "min":
          text = `Min value: 1`;
          break;
        case "matDatepickerMin":
          text =
            `Date cannot be before ` +
            pipe.transform(
              controlErrors.matDatepickerMin.min,
              shortDateFormat,
              "UTC"
            );
          break;
        case "matDatepickerMax":
          text =
            `Date cannot be past ` +
            pipe.transform(
              controlErrors.matDatepickerMax.max,
              shortDateFormat,
              "UTC"
            );
          break;
        case "max":
          text = `Max value: 1`;
          break;
        case "restrictWords":
          text = `code used: ${controlErrors[keyError]}`;
          break;
        default:
          if (key === "email") {
            text = "Invalid email";
          } else {
            text = `${key}: ${keyError}: ${controlErrors[keyError]}`;
          }
      }
    });
  }

  return text;
}

//here we write the same mathod which will provided inside reactive forms=validator.restrictWords(['ABC','PQR'])
export function restrictWords(words: string[]) {
  //creating referance of form control and providing string as argument
  return (control: FormControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    //first we check if there is any words that pass from input
    if (!words) return null;
    //we filter the array if this word present then store it into invalidWords
    const v = control.value.toString().toLowerCase();

    var invalidWords = words
      .map((w) => (v == w.toLowerCase() ? w : null))
      .filter((w) => w != null);

    return invalidWords && invalidWords.length > 0
      ? { restrictWords: invalidWords.join(", ") }
      : null;
  };
}

export const conditionalValidator = (predicate: () => boolean, validator: ValidatorFn): ValidatorFn => {
  return (formControl) => {
    if (!formControl.parent) {
      return null;
    }
    if (predicate()) {
      return validator(formControl);
    }
    return null;
  };
};

function createPatternValidator(
  pattern: RegExp,
  errorKey: string
): ValidatorFn {
  return (control: FormControl): ValidationErrors | null => {
    if (!control.value || pattern.test(control.value)) {
      return null;
    }
    const errors = {};
    errors[errorKey] = true;
    return errors;
  };
}

export class ValidationExtensions {
  /**
   * Touches each control of the form group, which will cause
   * the validators to be run.
   */
  static validateAll(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAll(control);
      }
    });

    formGroup.markAllAsTouched();
  }

  /**
   * Recursively gets all the errors on a form and returns an object.
   */
  static getFormErrors(form: AbstractControl) {
    if (form instanceof FormControl) {
      // Return FormControl errors or null
      return form.errors ?? null;
    }
    if (form instanceof FormGroup) {
      const groupErrors = form.errors;
      // Form group can contain errors itself, in that case add 'em
      const formErrors = groupErrors ? { groupErrors } : {};
      Object.keys(form.controls).forEach((key) => {
        // Recursive call of the FormGroup fields
        const error = this.getFormErrors(form.get(key));
        if (error !== null) {
          // Only add error if not null
          formErrors[key] = error;
        }
      });
      // Return FormGroup errors or null
      return Object.keys(formErrors).length > 0 ? formErrors : null;
    }
  }
}
